import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { useLocation, useNavigate } from 'react-router';

import { LoadingIndicator } from '@sorare/core/src/atoms/loader/LoadingIndicator';
import { Dialog } from '@sorare/core/src/components/dialog';
import { DialogKey } from '@sorare/core/src/components/navigation/WithDialogs';
import { idFromObject } from '@sorare/core/src/gql/idFromObject';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';
import { useDialogParam } from '@sorare/core/src/hooks/navigation/useDialogParam';
import { isType } from '@sorare/core/src/lib/gql';

import { GameView } from 'components/game/GameView';

import type {
  BaseballGameViewDialogQuery,
  BaseballGameViewDialogQueryVariables,
} from './__generated__/index.graphql';

const GAME_VIEW_DIALOG_QUERY = gql`
  query BaseballGameViewDialogQuery($gameId: ID!, $gameIdWithoutPrefix: ID!) {
    anyGame(id: $gameId) {
      id
      ...GameView_GameOfBaseball
    }
  }
  ${GameView.fragments.GameOfBaseball}
` as TypedDocumentNode<
  BaseballGameViewDialogQuery,
  BaseballGameViewDialogQueryVariables
>;

type Props = {
  open: boolean;
  onClose?: () => void;
  gameId?: string;
};

export const GameViewDialog = ({
  open,
  onClose,
  gameId: gameIdFromProps,
}: Props) => {
  const gameIdDialogParameter = useDialogParam(DialogKey.game);
  const gameId = gameIdDialogParameter || gameIdFromProps;
  const navigate = useNavigate();
  const location = useLocation();
  const goBack = () => navigate(location.pathname);

  const { data, loading } = useQuery(GAME_VIEW_DIALOG_QUERY, {
    variables: {
      gameId: `Game:${idFromObject(gameId!)}`,
      gameIdWithoutPrefix: idFromObject(gameId!),
    },
    skip: !gameId,
  });

  const { anyGame } = data || {};

  return (
    <Dialog hideHeader open={open} onClose={onClose ?? goBack} fullScreen>
      <>
        {!!loading && <LoadingIndicator />}
        {isType(anyGame, 'GameOfBaseball') && <GameView game={anyGame} />}
      </>
    </Dialog>
  );
};
